import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { PlainText, Source, SourceMaterialsMediaObject, Website } from 'types'

interface Data {
  source: Source
}

type Variables = (
  | Pick<PlainText, 'text' | 'type'>
  | Pick<Website, 'url' | 'type'>
  | (Pick<SourceMaterialsMediaObject, 'type'> & { media_object_id: number })
) & { signal?: AbortSignal }

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

const createSource = async ({ workspaceId, signal, ...variables }: Variables & { workspaceId: number }) => {
  const res = await sendToApi<{ source: Source }>(`/w/${workspaceId}/website_crawls`, {
    method: 'POST',
    body: variables,
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    throw new Error(res.data)
  }

  return res.data
}

export const useCreateWebsiteCrawl = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>(async (vars) =>
    createSource({
      workspaceId,
      ...vars,
    }),
  )
}
